import React, { useEffect, useRef, useState } from 'react';

/**
 * Fires once when the element first comes near the viewport, for scroll
 * reveals. One-shot on purpose: re-animating on the way back up reads as a
 * glitch rather than an effect.
 *
 * Lifted here from squarebit-subdivs and squarebit-eye, which each carried an
 * identical private copy.
 */
export function useInViewOnce<T extends Element>(): [React.RefObject<T>, boolean] {
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || inView) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setInView(true);
      },
      { rootMargin: '200px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [inView]);

  return [ref, inView];
}

export default useInViewOnce;
